import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import App from "./App";
import 'katex/dist/katex.min.css';
import {
  createBrowserRouter,
  RouterProvider,
  redirect,
  Outlet,
} from "react-router-dom";

import { API_BASE_URL } from "./config";

import Register from "./pages/register/register";
import SignIn from "./pages/signIn";
import Subjects from "./pages/subjects";
import ForgotPassword from "./pages/forgotPassword";
import ResetPassword from "./pages/resetPassword";
import ValidateAuthSession from "./components/ValidateAuthSession";

import PastPapers from "./pages/pastpapers";
import WhatsNew from "./pages/whatsNew";
import UserDashboard from "./pages/userDashboard";
import Admin from "./pages/admin";
import Progress from "./pages/progress";
import Teacher from "./pages/teacher";
import Profile from "./pages/profile";

// subject page imports
import Accounting from "./pages/subject-pages/accounting";
import Biology from "./pages/subject-pages/bgcse/biology/biology";
import Mathematics from './pages/subject-pages/bgcse/mathematics/mathematics';
import Chemistry from "./pages/subject-pages/chemistry";
import Commerce from "./pages/subject-pages/commerce";
import EnglishLanguage from "./pages/subject-pages/english-language";
import Geography from "./pages/subject-pages/geography";
import History from "./pages/subject-pages/history";
import Literature from "./pages/subject-pages/literature";
import Physics from "./pages/subject-pages/physics";
import Religion from "./pages/subject-pages/religion";

import BiologyQuizzes from "./pages/subject-pages/bgcse/biology/quizzes";
import MathematicsQuizzes from "./pages/subject-pages/bgcse/mathematics/quizzes";

// biology unit imports
import CharOfLiving from "./pages/subject-pages/bgcse/biology/biology-units/CharOfLiving";
import BinomialSystem from "./pages/subject-pages/bgcse/biology/biology-units/BinomialSystem";
import Ecology from "./pages/subject-pages/bgcse/biology/biology-units/Ecology";
import BahamianEcosystem from "./pages/subject-pages/bgcse/biology/biology-units/BahamianEcosystem";

// module learn import
import ModuleLearn from "./components/subject-components/ModuleLearn";
import ModuleLearnSummary from "./components/subject-components/ModuleLearnSummary";

// biology module imports
import EnvironmentalBiology from "./pages/subject-pages/bgcse/biology/biology-modules/environmental-biology";
import CellBiologyAndGenetics from "./pages/subject-pages/bgcse/biology/biology-modules/cell-biology-and-genetics";
import NutritionAndFoodSupply from "./pages/subject-pages/bgcse/biology/biology-modules/nutrition-and-food-supply";
import PlantAnatomyAndPhysiology from "./pages/subject-pages/bgcse/biology/biology-modules/plant-anatomy-and-physiology";
import HumanAnatomyAndPhysiology from "./pages/subject-pages/bgcse/biology/biology-modules/human-anatomy-and-physiology";

// math module imports
import NumberAndOperations from "./pages/subject-pages/bgcse/mathematics/math-modules/number-and-operations";
import Algebra from "./pages/subject-pages/bgcse/mathematics/math-modules/algebra";
import GeometryAndSpatialSense from "./pages/subject-pages/bgcse/mathematics/math-modules/geometry-and-spatial-sense";
import Measurement from "./pages/subject-pages/bgcse/mathematics/math-modules/measurement";
import StatisticsAndProbability from "./pages/subject-pages/bgcse/mathematics/math-modules/statistics-and-probability";

// admin imports
import QuestionAudit from "./pages/admin-pages/questionAudit";
import AdminLogin from "./pages/admin-pages/adminLogin";
import Analytics from "./pages/admin-pages/analytics";
import RoboAdmin from "./pages/admin-pages/roboAdmin";

axios.interceptors.request.use((config) => {
  const isAdminApi = config.url?.includes("/api/admin");
  const tokenKey = isAdminApi ? "admin_auth_token" : "auth_token";
  const token = localStorage.getItem(tokenKey) || localStorage.getItem("admin_auth_token");

  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// helper wrappers
const protectedRoute = (element) => (
  <ValidateAuthSession
    tokenKey="auth_token"
    validatePath="/api/validate"
    redirectTo="/signin"
    onUnauthed={() => {
      localStorage.removeItem("user");
    }}
  >
    {element}
  </ValidateAuthSession>
);

const adminProtectedRoute = (element) => (
  <ValidateAuthSession
    tokenKey="admin_auth_token"
    validatePath="/api/admin/validate"
    redirectTo="/admin-login"
    onUnauthed={() => {}}
  >
    {element}
  </ValidateAuthSession>
);

/**
 * ============================================================
 * ENROLLMENT GATES (added)
 * - Use loaders so users cannot directly visit /bgcse/biology/... URLs
 *   without being enrolled.
 * - You can reuse the param-based loader for /:subject/:module/learn
 * ============================================================
 */

// Map subject slug -> subject_id in your DB
const SUBJECT_IDS = {
  bgcse: { 
    biology: 1,
    mathematics: 10,
  },
  // bjc: {  // example for future subjects
  //   biology: 12 
  // },
};

const requireEnrollmentLoader =
  ({ subjectSlug, subjectId }) =>
  async ({ request }) => {
    const token = localStorage.getItem("auth_token");
    const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
    const studentId = userInfo?.id;

    const url = new URL(request.url);
    const nextPath = url.pathname + url.search;

    // Not logged in -> send to signin (keeps consistent with protectedRoute)
    if (!token || !studentId) {
      return redirect(`/signin?next=${encodeURIComponent(nextPath)}`);
    }

    try {
      const resp = await axios.get(`${API_BASE_URL}/enrollments`, {
        params: { student_id: studentId, subject_id: subjectId },
      });

      const enrolled = Array.isArray(resp.data) && resp.data.length > 0;
      if (!enrolled) {
        return redirect(`/subjects`);
      }

      return null;
    } catch (err) {
      const status = err?.response?.status;

      // If session is invalid, force re-auth
      if (status === 401 || status === 403) {
        localStorage.removeItem("user");
        localStorage.removeItem("auth_token");
        return redirect(`/signin?next=${encodeURIComponent(nextPath)}`);
      }

      // Default: treat as not enrolled
      return redirect(`/subjects?locked=${subjectSlug}`);
    }
  };

// For routes like "/:exam_type/:subject/:module/learn" and "/:exam_type/:subject/:module/learn-summary"
const requireEnrollmentFromParamsLoader = () => async ({ request, params }) => {
  const examSlug = String(params?.exam_type || "").toLowerCase();
  if (examSlug !== "bgcse" && examSlug !== "bjc") return redirect("/subjects");

  const subjectSlug = String(params?.subject || "").toLowerCase();
  const subjectId = SUBJECT_IDS[examSlug]?.[subjectSlug];

  if (!subjectId) return redirect("/subjects");

  return requireEnrollmentLoader({ subjectSlug, subjectId })({ request });
};

const router = createBrowserRouter([
  // PUBLIC
  { path: "/", element: <App /> },
  { path: "/register", element: <Register /> },
  { path: "/signin", element: <SignIn /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/reset-password", element: <ResetPassword /> },
  { path: "/admin-login", element: <AdminLogin /> },

  // LOGGED IN AUTH PROTECTED
  { path: "/progress", element: protectedRoute(<Progress />) },
  { path: "/teacher", element: protectedRoute(<Teacher />) },
  { path: "/profile", element: protectedRoute(<Profile />) },
  { path: "/whats-new", element: protectedRoute(<WhatsNew />) },
  { path: "/subjects", element: protectedRoute(<Subjects />) },
  { path: "/user-dashboard", element: protectedRoute(<UserDashboard />) },
  { path: "/accounting", element: protectedRoute(<Accounting />) },

  // BGCSE SUBJECT ROUTES (with enrollment gate)
  {
    path: "/bgcse/biology",
    element: protectedRoute(<Outlet />),
    loader: requireEnrollmentLoader({
      subjectSlug: "biology",
      subjectId: SUBJECT_IDS.bgcse.biology,
    }),
    children: [
      { index: true, element: <Biology /> },
      { path: "quizzes", element: <BiologyQuizzes /> },
      { path: "environmental-biology", element: <EnvironmentalBiology /> },
      { path: "cell-biology-and-genetics", element: <CellBiologyAndGenetics /> },
      { path: "nutrition-and-food-supply", element: <NutritionAndFoodSupply /> },
      { path: "plant-anatomy-and-physiology", element: <PlantAnatomyAndPhysiology />, },
      { path: "human-anatomy-and-physiology", element: <HumanAnatomyAndPhysiology />, },
    ],
  },

  {
    path: "/bgcse/mathematics",
    element: protectedRoute(<Outlet />),
    loader: requireEnrollmentLoader({
      subjectSlug: "mathematics",
      subjectId: SUBJECT_IDS.bgcse.mathematics,
    }),
    children: [
      { index: true, element: <Mathematics /> },
      { path: "quizzes", element: <MathematicsQuizzes /> },
      { path: "number-and-operations", element: <NumberAndOperations /> },
      { path: "algebra", element: <Algebra /> },
      { path: "geometry-and-spatial-sense", element: <GeometryAndSpatialSense /> },
      { path: "measurement", element: <Measurement /> },
      { path: "statistics-and-probability", element: <StatisticsAndProbability /> },
    ],
  },

  /**
   * ✅ LEARN ROUTES (added enrollment gate by params)
   * This blocks direct access like:
   * - /bgcse/biology/environmental-biology/learn
   * - /bgcse/biology/environmental-biology/learn-summary
   */
  {
    path: "/:exam_type/:subject/:module/learn",
    element: protectedRoute(<ModuleLearn />),
    loader: requireEnrollmentFromParamsLoader(),
  },
  {
    path: "/:exam_type/:subject/:module/learn-summary",
    element: protectedRoute(<ModuleLearnSummary />),
    loader: requireEnrollmentFromParamsLoader(),
  },

  // ADMIN PROTECTED
  { path: "/question-audit", element: adminProtectedRoute(<QuestionAudit />) },
  { path: "/analytics",      element: adminProtectedRoute(<Analytics />) },
  { path: "/robo-admin",     element: adminProtectedRoute(<RoboAdmin />) },

  /**
   * ------------------------------------------------------------
   * The original Biology routes are left here as comments
   * (so nothing is "removed"), but they should stay disabled
   * to avoid duplicate route paths.
   * ------------------------------------------------------------
   */

  // { path: "/bgcse/biology/quizzes", element: protectedRoute(<BiologyQuizzes />) },
  // { path: "/bgcse/biology", element: protectedRoute(<Biology />) },
  // {
  //   path: "/bgcse/biology/environmental-biology",
  //   element: protectedRoute(<EnvironmentalBiology />),
  // },
  // {
  //   path: "/bgcse/biology/cell-biology-and-genetics",
  //   element: protectedRoute(<CellBiologyAndGenetics />),
  // },
  // {
  //   path: "/bgcse/biology/nutrition-and-food-supply",
  //   element: protectedRoute(<NutritionAndFoodSupply />),
  // },
  // {
  //   path: "/bgcse/biology/plant-anatomy-and-physiology",
  //   element: protectedRoute(<PlantAnatomyAndPhysiology />),
  // },
  // {
  //   path: "/bgcse/biology/human-anatomy-and-physiology",
  //   element: protectedRoute(<HumanAnatomyAndPhysiology />),
  // },

  // { path: "/pastpapers", element: protectedRoute(<PastPapers />) },
  // {
  //   path: "/chemistry",
  //   element: protectedRoute(<Chemistry />),
  // },
  // {
  //   path: "/english-language",
  //   element: protectedRoute(<EnglishLanguage />),
  // },
  // {
  //   path: "/geography",
  //   element: protectedRoute(<Geography />),
  // },
  // {
  //   path: "/history",
  //   element: protectedRoute(<History />),
  // },
  // {
  //   path: "/literature",
  //   element: protectedRoute(<Literature />),
  // },
  // {
  //   path: "/mathematics",
  //   element: protectedRoute(<Mathematics />),
  // },
  // {
  //   path: "/physics",
  //   element: protectedRoute(<Physics />),
  // },
  // {
  //   path: "/religion",
  //   element: protectedRoute(<Religion />),
  // },
  // {
  //   path: "/commerce",
  //   element: protectedRoute(<Commerce />),
  // },

  // {
  //   path: "/admin",
  //   element: protectedRoute(<Admin />),
  // },

  // {
  //   path: "biology/characteristics-of-living-organisms",
  //   element: protectedRoute(<CharOfLiving />),
  // },
  // {
  //   path: "biology/binomial-system",
  //   element: protectedRoute(<BinomialSystem />),
  // },
  // {
  //   path: "biology/ecology",
  //   element: protectedRoute(<Ecology />),
  // },
  // {
  //   path: "biology/bahamian-ecosystems",
  //   element: protectedRoute(<BahamianEcosystem />),
  // },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<RouterProvider router={router} />);
